import { createRoot } from 'react-dom/client';
import { AskForLocationPrompt } from './AskForLocationPrompt.js';
import { MapSearchResults } from './MapSearchResults.js';
import {
    getInitialResultFiltersFromUrl,
    getStartingDisplayOnlyClassroomEssentialsFromUrl,
    Helpers,
    MIN_ZOOM,
} from './MapSearchUtils.js';
import { isValidZip } from '../utils/addressUtils.ts';

/* global google readCookie getURLParam isMobileDevice */

function updateMapHeight() {
    const headerHeight = document.getElementById('mainHeader').offsetHeight;
    const windowHeight = window.innerHeight;
    const setHeight = windowHeight - headerHeight;
    $('#content').height('updateMapHeight' + setHeight);
}
function getMapHeight() {
    const headerHeight = document.getElementById('mainHeader').offsetHeight;
    const windowHeight = window.innerHeight;
    const setHeight = windowHeight - headerHeight;
    $('#content').height(setHeight);
    return `${setHeight}px`;
}

const getFeatureFlags = () => {
    return JSON.parse(document.getElementById('js-init-data').textContent);
}

const processInitialRenderAndSearch = function (startingLat, startingLng) {
    document.getElementById('react-content-fallback').style.display = 'none';
    const defaultStartingLatLng = Helpers.getDefaultStartingLatLngBasedOnSystemTime();
    let centralLat = defaultStartingLatLng.lat;
    let centralLng = defaultStartingLatLng.lng;
    if (startingLat && startingLng) {
        centralLat = startingLat;
        centralLng = startingLng;
    }
    const map = new google.maps.Map(document.getElementById('map'), {
        center: { lat: centralLat, lng: centralLng },
        zoom: 19,
        zoomControl: !isMobileDevice,
        zoomControlOptions: {
            style: google.maps.ZoomControlStyle.LARGE,
            position: google.maps.ControlPosition.LEFT_BOTTOM,
        },
        minZoom: MIN_ZOOM,
        maxZoom: 19,
        clickableIcons: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
        scrollwheel: true,
        styles:
            [
                {
                    "featureType": "administrative",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "visibility": "off"
                        }
                    ]
                },
                {
                    "featureType": "administrative",
                    "elementType": "labels",
                    "stylers": [
                        {
                            "color": "#9f887a"
                        },
                        {
                            "visibility": "on"
                        }
                    ]
                },
                {
                    "featureType": "administrative",
                    "elementType": "labels.text.stroke",
                    "stylers": [
                        {
                            "color": "#fff8f1"
                        },
                        {
                            "visibility": "on"
                        },
                        {
                            "weight": 6
                        }
                    ]
                },
                {
                    "featureType": "landscape",
                    "stylers": [
                        {
                            "color": "#fff7f1"
                        }
                    ]
                },
                {
                    "featureType": "landscape",
                    "elementType": "labels.text",
                    "stylers": [
                        {
                            "color": "#9f887a"
                        },
                        {
                            "visibility": "simplified"
                        }
                    ]
                },
                {
                    "featureType": "poi",
                    "stylers": [
                        {
                            "visibility": "off"
                        }
                    ]
                },
                {
                    "featureType": "road",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#f1e7e0"
                        },
                        {
                            "visibility": "simplified"
                        }
                    ]
                },
                {
                    "featureType": "road",
                    "elementType": "labels.icon",
                    "stylers": [
                        {
                            "visibility": "off"
                        }
                    ]
                },
                {
                    "featureType": "road",
                    "elementType": "labels.text",
                    "stylers": [
                        {
                            "color": "#9f887a"
                        }
                    ]
                },
                {
                    "featureType": "road",
                    "elementType": "labels.text.stroke",
                    "stylers": [
                        {
                            "color": "#ecdfd5"
                        },
                        {
                            "visibility": "simplified"
                        }
                    ]
                },
                {
                    "featureType": "transit",
                    "stylers": [
                        {
                            "visibility": "off"
                        }
                    ]
                },
                {
                    "featureType": "water",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#bbe7ec"
                        }
                    ]
                },
                {
                    "featureType": "water",
                    "elementType": "labels.text",
                    "stylers": [
                        {
                            "visibility": "simplified"
                        }
                    ]
                }
            ]

    });

    const drawingManager = new google.maps.drawing.DrawingManager({
        drawingControl: false,
        markerOptions: {
            icon: MapSearchResults.MARKER_ICON,
        },
    });
    drawingManager.setMap(map);

    class HtmlMapMarker extends google.maps.OverlayView {
        constructor(args) {
            super();
            this.latlng = args.latlng;
            this.html = args.html;
            this.numProjectResults = args.numProjectResults;
            this.numEssentialsResults = args.numEssentialsResults;
            this.schoolsAtGeopoint = args.schoolsAtGeopoint;
            this.schoolNames = args.schoolNames;
            this.distanceFromSearchCenter = args.distanceFromSearchCenter;
            this.isSelected = args.isSelected;
            this.minCostToComplete = args.minCostToComplete;
        }

        createDiv() {
            this.div = document.createElement('div');
            this.div.style.position = 'absolute';
            if (this.html) {
                this.div.innerHTML = this.html;
            }

            this.getPanes().overlayLayer.appendChild(this.div);
            this.getPanes().overlayMouseTarget.appendChild(this.div);

            this.div.addEventListener('click', event => {
                google.maps.event.trigger(this, 'click');
            });
        }

        positionDiv() {
            const point = this.getProjection().fromLatLngToDivPixel(this.latlng);
            if (point) {
                this.div.style.left = `${point.x}px`;
                this.div.style.top = `${point.y}px`;
            }
        }

        draw() {
            if (!this.div) {
                this.createDiv();
            }
            this.positionDiv();
        }

        remove() {
            if (this.div) {
                this.div.parentNode.removeChild(this.div);
                this.div = null;
            }
        }

        update() {
            if (this.html && this.div) {
                this.div.innerHTML = this.html;
            }
        }

        getPosition() {
            return this.latlng;
        }

        getDraggable() {
            return false;
        }
    }

    // The map itself is outside the React tree, but sometimes it (or the parent div it shares with the React tree) must
    // be updated in response to React-managed events. These functions let you do that.
    const parentModifierCallbacks = {
        setPointOpen: (value) => {
            if (value) {
                $("#content").addClass('point-open');
            } else {
                $("#content").removeClass('point-open');
            }
        },
        setFullscreen: (value) => {
            if (value) {
                $("#content").addClass('full-screen');
            } else {
                $("#content").removeClass('full-screen');
            }
        },
        setMinimizePoints: (value) => {
            if (value) {
                $('#content').addClass('minimize-points');
            } else {
                $('#content').removeClass('minimize-points');
            }
        },
        togglePickedUp: () => {
            $("#content").toggleClass("picked-up");
        }
    }

    // We've already chosen an initial lat/long at this point, so these params will only be used to make sure we
    // maintain location-params in links back to list-search.
    const initialSearchLocationParams = Helpers.getNewSearchParamsFromLocationSearchUrl(window.location.href).newLocationSearchParams;
    const featureFlags = getFeatureFlags();
    const startingResultFilters = getInitialResultFiltersFromUrl(window.location.href);
    const startingDisplayOnlyClassroomEssentials = getStartingDisplayOnlyClassroomEssentialsFromUrl(window.location.href);
    const root = createRoot(document.getElementById('react-content'));

    root.render(<MapSearchResults
        map={map}
        startingLat={centralLat}
        startingLng={centralLng}
        HtmlMapMarker={HtmlMapMarker}
        isMobile={isMobileDevice}
        getMapHeight={getMapHeight}
        initialHeight={getMapHeight()}
        essentialsListEnabled={featureFlags.essentialsListEnabled}
        schoolDonationEnabled={featureFlags.schoolDonationEnabled}
        initialLocationSearchParams={initialSearchLocationParams}
        parentModifierCallbacks={parentModifierCallbacks}
        startingResultFilters={startingResultFilters}
        startingDisplayOnlyClassroomEssentials={startingDisplayOnlyClassroomEssentials}
    />);
};

const renderMap = function () {
    updateMapHeight();

    if (window.location && window.location.href) {
        const startingZipParam = getURLParam(window.location.href, 'startingZip');
        if (isValidZip(startingZipParam)) {
            return lookupAndRenderAddress(startingZipParam , false);
        }
        const cityName = getURLParam(window.location.href, 'cityName');
        const state = getURLParam(window.location.href, 'state');
        if (cityName && state) {
            return lookupAndRenderAddress(`${cityName}, ${state}`, false);
        }

        let centerLat;
        let centerLng;
        if (state) {
            const searchParamsForState = Helpers.getDefaultLatLngForStateCode(state);
            centerLat = searchParamsForState.lat;
            centerLng = searchParamsForState.lng;
        } else {
            centerLat = getURLParam(window.location.href, 'centerLat');
            centerLng = getURLParam(window.location.href, 'centerLng');
        }
        if (centerLat && centerLng && !isNaN(Number(centerLat)) && !isNaN(Number(centerLng))) {
            return processInitialRenderAndSearch(Number(centerLat), Number(centerLng));
        }
    }
    const geoCookie = JSON.parse(readCookie('geoLocation'));
    if (geoCookie && geoCookie.latitude && geoCookie.longitude) {
        return processInitialRenderAndSearch(geoCookie.latitude, geoCookie.longitude);
    }

    const root = createRoot(document.getElementById('react-content-fallback'));

    root.render(<AskForLocationPrompt
        processInitialRenderAndSearch={processInitialRenderAndSearch}
    />);
};

const lookupAndRenderAddress = function (addressString, unmountInputComponent) {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({address: addressString}, function (results, status) {
        if (unmountInputComponent) {
            const root = createRoot(document.getElementById('react-content-fallback'));
            root.unmount();
            if (isMobileDevice) {
                $('html, body').animate({ scrollTop: $('#mainHeader').offset().top });
            }
        }
        if (status == 'OK' && results) {
            const place = results[0];
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            processInitialRenderAndSearch(lat, lng);
        } else {
            console.log('Geocode was not successful for the following reason: ' + status, results);
            const defaultStartingLatLng = Helpers.getDefaultStartingLatLngBasedOnSystemTime();
            processInitialRenderAndSearch(defaultStartingLatLng.lat, defaultStartingLatLng.lng);
        }
    });
};

// Set so that the Google Maps API can use this as a callback. See the script tag in map_search.jsp.
window.renderMap = renderMap;
